import Login from "./components/Login.vue";
import LoginForgotPassword from "./components/LoginForgotPassword.vue";
import LoginForgotUsername from "./components/LoginForgotUsername.vue";
import LoginVerify from "./components/LoginVerify.vue";
import UpdatePassword from "./components/UpdatePassword.vue";
import CognitoMfaVerify from "@/shared/components/CognitoMfaVerify.vue";
import { AuthType, MfaStatusEnum } from "@/shared/types";
import LostMfaDevice from "@/shared/components/LostMfaDevice.vue";
import HsabLogin from "./components/HsabLogin.vue";
import AdminPortalOktaLogin from "./components/AdminPortalOktaLogin.vue";
import BiometricAuthSetup from "./components/BiometricAuthSetup.vue";
import { Config } from "@/shared/config";
import { domain as hsaBankDomain } from "@/shared/hsaBankUrls";
import { ForgotUsernameResources } from "@/shared/forgotUsernameResources";

const routeToName = (routeName: string) => ({ name: routeName });

const isOktaMobile = from => {
  return Config && Config.Mobile && Config.Mobile.AuthType === AuthType.Okta && from.name !== LoginRouteNames.OktaLogin;
};

const isHsaBankDomain = () => {
  return window.location.hostname.includes(hsaBankDomain);
};

export const LoginRouteNames = Object.freeze({
  Login : "login",
  ForgotPassword: "forgotPassword",
  ForgotUsername: "forgotUsername",
  ForgotPasswordVerify: "forgotPasswordVerify",
  UpdatePassword: "updatePassword",
  MfaVerify: "MfaVerify",
  LostDevice: "LostDevice",
  OktaLogin: "OktaLogin",
  AdminOktaLogin: "AdminOktaLogin",
  BiometricAuthSetup: "biometricAuthSetup",
  NewUser: "newUser"
});

export const LoginRoutes = [
  {
    path: "/login",
    name: LoginRouteNames.Login,
    component: Login,
    meta: {
      allowAnonymous: true,
      hide_footer: true,
      hide_header: true,
      hide_left_nav: true
    },
    beforeEnter: (to, from, next) => {
      if (isOktaMobile(from) || isHsaBankDomain()) {
        next({ name: LoginRouteNames.OktaLogin, query: to.query });
      } else {
        next();
      }
    },
    props: route => ({
      routeToForgotPassword: routeToName(LoginRouteNames.ForgotPassword),
      routeToUpdatePassword: (username, originalPassword) => ({
        name: LoginRouteNames.UpdatePassword,
        params: {
          username,
          originalPassword
        }
      }),
      MfaRouteFunc: (username: string, password: string, mfaStatus: MfaStatusEnum, setupBiometricAuth: boolean) => ({
        name: LoginRouteNames.MfaVerify,
        params: {
          Username: username,
          Password: password,
          MfaType: mfaStatus,
          SetupBiometricAuth: setupBiometricAuth
        },
        query: (route.query && route.query.returnUrl ? {
          returnUrl: route.query.returnUrl
        } : null )}),
      routeToBiometricAuthSetup: (username, password) => ({
        name: LoginRouteNames.BiometricAuthSetup,
        params: {
          username,
          password
        }
      }),
    })
  },
  {
    path: "/updatePassword",
    name: LoginRouteNames.UpdatePassword,
    component: UpdatePassword,
    meta: {
      allowAnonymous: true,
      hide_footer: true,
      hide_header: true,
      hide_left_nav: true
    },
    props: route => ({
      username: route.params.username,
      originalPassword: route.params.originalPassword,
      MfaRouteFunc: (username: string, password: string, mfaStatus: MfaStatusEnum) => ({
        name: LoginRouteNames.MfaVerify,
        params: {
          Username: username,
          Password: password,
          MfaType: mfaStatus,
        },
        query: (route.query && route.query.returnUrl ? {
          returnUrl: route.query.returnUrl
        } : null )})
    })
  },
  {
    path: "/forgot-password",
    name: LoginRouteNames.ForgotPassword,
    component: LoginForgotPassword,
    meta: {
      allowAnonymous: true,
      hide_footer: true,
      hide_header: true,
      hide_left_nav: true
    },
    props: {
      routeToForgotPasswordVerify: username => ({
        name: LoginRouteNames.ForgotPasswordVerify,
        params: {
          username
        }
      }),
      routeToLogin: routeToName(LoginRouteNames.Login),
      routeToOktaLogin: routeToName(LoginRouteNames.OktaLogin)
    }
  },
  {
    path: "/forgot-username",
    name: LoginRouteNames.ForgotUsername,
    component: LoginForgotUsername,
    meta: {
      allowAnonymous: true,
      hide_footer: true,
      hide_header: true,
      hide_left_nav: true
    },
    props: route => ({
      routeToOktaLogin: routeToName(LoginRouteNames.OktaLogin)
    })
  },
  {
    path: "/new-user",
    name: LoginRouteNames.NewUser,
    component: LoginForgotUsername,
    meta: {
      allowAnonymous: true,
      hide_footer: true,
      hide_header: true,
      hide_left_nav: true
    },
    props: route => ({
      routeToOktaLogin: routeToName(LoginRouteNames.OktaLogin),
      header: ForgotUsernameResources.NewUserHeader,
      description: ForgotUsernameResources.NewUserDescription
    })
  },
  {
    path: "/forgot-password-verify/:username",
    name: LoginRouteNames.ForgotPasswordVerify,
    component: LoginVerify,
    meta: {
      allowAnonymous: true,
      hide_footer: true,
      hide_header: true,
      hide_left_nav: true
    },
    props: route => ({
      username: route.params.username,
      MfaRouteFunc: (username: string, password: string, mfaStatus: MfaStatusEnum) => ({
        name: LoginRouteNames.MfaVerify,
        params: {
          Username: username,
          Password: password,
          MfaType: mfaStatus,
        },
        query: (route.query && route.query.returnUrl ? {
          returnUrl: route.query.returnUrl
        } : null )})
    })
  },
  {
    path: "/mfa",
    name: LoginRouteNames.MfaVerify,
    component: CognitoMfaVerify,
    meta: {
      allowAnonymous: true,
      hide_footer: true,
      hide_header: true,
      hide_left_nav: true
    },
    props: route => ({
      Username: route.params.Username,
      Password: route.params.Password,
      MfaType: route.params.MfaType,
      FailureRouteFunc: () => ({
        name: LoginRouteNames.Login,
        query: (route.query && route.query.returnUrl ? { returnUrl: route.query.returnUrl } : null )
      }),
      LostDeviceRouteFunc: () => ({
        name: LoginRouteNames.LostDevice
      }),
      SetupBiometricAuth: route.params.SetupBiometricAuth,
      routeToBiometricAuthSetup: (username, password) => ({
        name: LoginRouteNames.BiometricAuthSetup,
        params: {
          username,
          password
        }
      }),
    })
  },
  {
    path: "/lost-device",
    name: LoginRouteNames.LostDevice,
    component: LostMfaDevice,
    meta: {
      allowAnonymous: true,
      hide_footer: true,
      hide_header: true,
      hide_left_nav: true
    }
  },
  {
    path: "/auth/login",
    name: LoginRouteNames.OktaLogin,
    component: HsabLogin,
    meta: {
      allowAnonymous: true,
      hide_footer: true,
      hide_header: true,
      hide_left_nav: true
    },
    props: route => ({
      routeToForgotPassword: { name: LoginRouteNames.ForgotPassword, query: { authType: AuthType.Okta } },
      routeToForgotUsername: { name: LoginRouteNames.ForgotUsername },
      routeToNewUser: { name: LoginRouteNames.NewUser }
    })
  },
  {
    path: "/admin/login",
    name: LoginRouteNames.AdminOktaLogin,
    component: AdminPortalOktaLogin,
    meta: {
      allowAnonymous: true,
      hide_footer: true,
      hide_header: true,
      hide_left_nav: true
    },
    props: route => ({
      routeToForgotPassword: { name: LoginRouteNames.ForgotPassword, query: { authType: AuthType.Okta } },
      routeToForgotUsername: { name: LoginRouteNames.ForgotUsername },
      routeToNewUser: { name: LoginRouteNames.NewUser }
    })
  },
  {
    path: "/biometricAuthSetup",
    name: LoginRouteNames.BiometricAuthSetup,
    component: BiometricAuthSetup,
    meta: {
      allowAnonymous: true,
      hide_footer: true,
      hide_header: true,
      hide_left_nav: true
    },
    props: route => ({
      username: route.params.username,
      password: route.params.password,
      FailureRouteFunc: () => ({
        name: LoginRouteNames.Login,
        query: (route.query && route.query.returnUrl ? { returnUrl: route.query.returnUrl } : null )
      }),
      routeToLogin: routeToName(LoginRouteNames.Login),
    })
  },
];